import { Platform } from 'react-native';
import { colors } from '../../../theme/colors';
import { withAlpha } from '../../../theme/colorUtils';
import { tokens } from '../../../theme/tokens';
import type { CalendarEventSource, CalendarEventStatus } from '../../../types/calendar';
import { EVENT_BLOCK_GAP } from './calendarEventLayout';
import { HOUR_HEIGHT } from './calendarHourGrid';

/**
 * How an event block should look. Pure data — no React, no pixels — so both
 * the positioned day/week blocks and the (flowing) agenda row can render the
 * same event the same way without either owning the lookup.
 *
 * The split is deliberate:
 *   SOURCE  -> hue.       Who put this on my calendar?
 *   STATUS  -> treatment. Is it still happening?
 *
 * Neither may ride on colour alone. Source pairs its hue with the glyph
 * `EventSourceIndicator` already renders; status pairs its treatment with
 * `statusLabelKey`, a real translated word. An 8px tint measures 2.5-2.9:1
 * against the app canvas, under the 3:1 WCAG 1.4.11 asks of a non-text
 * indicator, so the colour is an accelerator and never the message.
 */
export interface EventAppearance {
  fill: string;
  borderColor: string;
  titleColor: string;
  metaColor: string;
  /** Cancelled only. Survives Android, where a dashed border silently renders solid. */
  strikeThrough: boolean;
  /** Translation key for the status chip, or null for the unremarkable default. */
  statusLabelKey: string | null;
  statusTone: EventStatusTone;
  /** Too short to carry anything but its title — see COMPACT_EVENT_THRESHOLD_MINUTES. */
  compact: boolean;
  /** Room for the time range below the title. False also drops on a compact block. */
  showTime: boolean;
  /**
   * Where the status word renders, or whether it renders at all.
   *
   *  - 'badge'  — its own row below the title (STATUS_JOIN_CONTENT_HEIGHT).
   *  - 'inline' — folded into the title row instead, at zero extra height,
   *               for a block too short for a dedicated row. A status is the
   *               ONLY non-colour signal `confirmed` and `no_show` carry, so
   *               it may never fall back to 'none' just because the chip
   *               doesn't fit — see the module doc.
   *  - 'none'   — nothing to show (scheduled has no label), or the block is
   *               `compact` and the status still reaches a screen reader
   *               through the Pressable's accessibilityLabel.
   */
  statusPlacement: 'badge' | 'inline' | 'none';
  /** Room for the Join button. Outranks status and time when space is tight. */
  showJoin: boolean;
}

export type EventStatusTone = 'none' | 'success' | 'error' | 'muted';

/**
 * Under this, a block has room for one line of title and nothing else. At the
 * day grid's 60px hour a 30-minute event is 30px tall; a time row and a chip
 * underneath the title would be clipped, not shrunk.
 */
export const COMPACT_EVENT_THRESHOLD_MINUTES = 45;

export function isCompactEvent(durationMinutes: number): boolean {
  return durationMinutes < COMPACT_EVENT_THRESHOLD_MINUTES;
}

/**
 * What the block can hold, measured against the block that is ACTUALLY drawn.
 *
 * The heights below come from the tokens the real render tree uses, so this
 * stays honest when a token moves — see CalendarDayEventBlock.tsx for the
 * elements they correspond to:
 *
 *   - `cardSurface()` sets `borderWidth: 1` (theme/surfaces.ts): 1px top and
 *     1px bottom. The card's `Box` adds `padding="xxs"` on both edges.
 *   - `VStack spacing="xxs"` puts one gap between each pair of stacked rows.
 *   - Title and time are `Text variant="caption"`, line height
 *     `fontSize.xs * lineHeight.normal` (theme/typography.ts).
 *   - `Badge size="sm"` fixes its own text `lineHeight: 16` plus
 *     `paddingVertical: spacing.xxs` (components/primitives/Badge.tsx).
 *   - `Button` forces `minHeight: tokens.interaction.minimumTarget` — 44 on
 *     iOS/web, **48 on Android**, so this is platform-keyed.
 *
 * Priority, most-protected first, is Join > status > time: on a time grid the
 * block's vertical position already encodes *when* the meeting is, so the time
 * row is the most redundant thing on screen, while the status is encoded
 * nowhere else once its chip is gone (see `statusPlacement`).
 */
export const CARD_CHROME_HEIGHT = 2 + tokens.spacing.xxs * 2; // 1px border + xxs padding, both edges.
export const ROW_GAP = tokens.spacing.xxs;
export const CAPTION_ROW_HEIGHT = tokens.fontSize.xs * tokens.lineHeight.normal;
export const STATUS_BADGE_ROW_HEIGHT = 16 + tokens.spacing.xxs * 2;
export const JOIN_ROW_HEIGHT = tokens.interaction.minimumTarget;

type TargetPlatform = keyof typeof JOIN_ROW_HEIGHT;

function currentTargetPlatform(): TargetPlatform {
  return Platform.OS === 'android' ? 'android' : 'default';
}

/**
 * A conservative floor on the height the caller will actually give this block.
 *
 * `getEventBlockLayout` rounds `top` and `bottom` INDEPENDENTLY before
 * subtracting, so the drawn height is not a pure function of duration — it
 * also depends on where the event starts inside the window. Two 46-minute
 * meetings a minute apart can differ by a pixel. Across every offset the
 * discrepancy is bounded at exactly 1px (both rounds move by less than a
 * whole pixel), so one pixel of slack is enough to guarantee this never
 * over-promises, and one pixel is invisible.
 *
 * EVENT_BLOCK_GAP is subtracted for the same reason it is there: the layout
 * takes it out of every block so back-to-back events never touch. Comparing
 * budgets against the un-gapped height is what clipped the Join button.
 */
const ROUNDING_SLACK = 1;

function drawnBlockHeight(durationMinutes: number): number {
  return Math.round((durationMinutes / 60) * HOUR_HEIGHT) - EVENT_BLOCK_GAP - ROUNDING_SLACK;
}

interface SourceTint {
  fill: string;
  borderColor: string;
}

/**
 * 'manual' is the board's default rather than a marked state, matching
 * `EventSourceIndicator`, which renders no badge for it: an unmarked event
 * reads as "a person did this", the majority case and the quietest default.
 * A missing source resolves here too — the column is optional on the type
 * because existing call sites construct literals without it.
 */
const SOURCE_TINTS: Record<CalendarEventSource, SourceTint> = {
  manual: {
    fill: colors.primary.muted,
    borderColor: colors.primary.borderActive,
  },
  voice_agent: {
    fill: colors.accent.purple.bg,
    borderColor: withAlpha(colors.accent.purple.neon, 0.4),
  },
  platform_agent: {
    fill: withAlpha(colors.accent.blue[500], 0.14),
    borderColor: withAlpha(colors.accent.blue.neon, 0.4),
  },
  provider_sync: {
    fill: withAlpha(colors.neutral[400], 0.14),
    borderColor: withAlpha(colors.neutral[300], 0.4),
  },
};

interface StatusTreatment {
  statusLabelKey: string | null;
  statusTone: EventStatusTone;
  strikeThrough: boolean;
  /** Replaces the source fill/border entirely — a cancelled event is no longer "a voice-agent meeting". */
  overrideFill?: string;
  overrideBorderColor?: string;
  /**
   * De-emphasis is a TOKEN swap, never an `opacity`. Alpha compounds with the
   * translucent fill beneath it: secondary text at 0.7 alpha measures 3.97:1,
   * under the 4.5:1 AA floor. colors.neutral[400] holds ~5:1 on these fills.
   */
  mutedText?: boolean;
}

const STATUS_TREATMENTS: Record<CalendarEventStatus, StatusTreatment> = {
  scheduled: {
    statusLabelKey: null,
    statusTone: 'none',
    strikeThrough: false,
  },
  confirmed: {
    statusLabelKey: 'calendar.eventStatus.confirmed',
    statusTone: 'success',
    strikeThrough: false,
  },
  cancelled: {
    statusLabelKey: 'calendar.eventStatus.cancelled',
    statusTone: 'muted',
    strikeThrough: true,
    overrideFill: colors.surface.bento.fill,
    overrideBorderColor: colors.surface.bento.border,
    mutedText: true,
  },
  completed: {
    statusLabelKey: 'calendar.eventStatus.completed',
    statusTone: 'muted',
    strikeThrough: false,
    overrideFill: colors.surface.bento.fill,
    overrideBorderColor: colors.surface.bento.border,
    mutedText: true,
  },
  no_show: {
    statusLabelKey: 'calendar.eventStatus.noShow',
    statusTone: 'error',
    strikeThrough: false,
    overrideBorderColor: colors.status.error.border,
  },
};

export function getEventAppearance(
  source: CalendarEventSource | null | undefined,
  status: CalendarEventStatus,
  durationMinutes?: number,
): EventAppearance {
  const tint = SOURCE_TINTS[source ?? 'manual'] ?? SOURCE_TINTS.manual;
  const treatment = STATUS_TREATMENTS[status] ?? STATUS_TREATMENTS.scheduled;
  const compact = durationMinutes === undefined ? false : isCompactEvent(durationMinutes);
  // Unknown duration (the all-day strip) has no meaningful height to clip
  // against, so everything renders — the pre-existing default.
  const blockHeight = durationMinutes === undefined ? undefined : drawnBlockHeight(durationMinutes);

  // Filled in priority order, each row claiming its space before the next is
  // considered, so nothing can be promised room that an earlier row took.
  let used = CARD_CHROME_HEIGHT + CAPTION_ROW_HEIGHT;
  const claim = (rowHeight: number): boolean => {
    if (compact) return false;
    if (blockHeight !== undefined && used + ROW_GAP + rowHeight > blockHeight) return false;
    used += ROW_GAP + rowHeight;
    return true;
  };

  const showJoin = claim(JOIN_ROW_HEIGHT[currentTargetPlatform()]);

  let statusPlacement: EventAppearance['statusPlacement'];
  if (treatment.statusLabelKey === null) {
    // scheduled: nothing to show, so nothing to protect.
    statusPlacement = 'none';
  } else if (claim(STATUS_BADGE_ROW_HEIGHT)) {
    statusPlacement = 'badge';
  } else if (compact) {
    // No room for a second row at all. The word still reaches a screen reader
    // through the Pressable's accessibilityLabel, which folds it in
    // unconditionally — 'none' is a rendering decision, not a lost signal.
    statusPlacement = 'none';
  } else {
    // The chip's own row does not fit, but the status is the ONLY non-colour
    // signal `confirmed` and `no_show` carry, so it moves into the title row
    // rather than disappearing. Costs no height, so it never competes.
    statusPlacement = 'inline';
  }

  const showTime = claim(CAPTION_ROW_HEIGHT);

  return {
    fill: treatment.overrideFill ?? tint.fill,
    borderColor: treatment.overrideBorderColor ?? tint.borderColor,
    titleColor: treatment.mutedText ? colors.neutral[400] : colors.text.primary,
    metaColor: treatment.mutedText ? colors.neutral[400] : colors.text.secondary,
    strikeThrough: treatment.strikeThrough,
    statusLabelKey: treatment.statusLabelKey,
    statusTone: treatment.statusTone,
    compact,
    showTime,
    statusPlacement,
    showJoin,
  };
}
